import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Handshake, MapPin, Users, LogIn, Loader2, CheckCircle, Store, ArrowRight, AlertCircle,
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { resolveBackendImage } from '../utils/brandLogo';

const TIPO_LABEL = {
  feira: 'Feira', cooperativa: 'Cooperativa', associacao: 'Associação',
  grupo: 'Grupo', rede: 'Rede',
};

export default function ConviteColetivo() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [coletivo, setColetivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [myBiz, setMyBiz] = useState([]);
  const [bizLoading, setBizLoading] = useState(false);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(null);

  useEffect(() => {
    api.get(`/v2/coletivos/convite/${token}`)
      .then((r) => setColetivo(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!user || !coletivo) return;
    setBizLoading(true);
    api.get('/v2/businesses/me/businesses')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setMyBiz(list);
        const elegiveis = list.filter((b) => b.municipio_id === coletivo.municipio_id);
        if (elegiveis.length === 1) setSelectedBiz(elegiveis[0].id);
      })
      .catch(() => setMyBiz([]))
      .finally(() => setBizLoading(false));
  }, [user, coletivo]);

  const handleAceitar = async () => {
    if (!selectedBiz) {
      toast.error('Selecione o negócio que vai entrar no coletivo');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/v2/coletivos/convite/${token}/aceitar`, { negocio_id: selectedBiz });
      setJoined(r.data);
      toast.success(r.data?.message || 'Você entrou no coletivo!');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao aceitar o convite');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="convite-invalido">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Convite inválido ou expirado</h1>
          <p className="text-gray-600 text-sm mb-6">
            Peça um novo link ao responsável do coletivo, ou conheça os coletivos ativos da cidade.
          </p>
          <Link to="/coletivos" className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700">
            Ver coletivos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="convite-sucesso">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Bem-vindo ao coletivo!</h1>
          <p className="text-gray-600 text-sm mb-6">
            Seu negócio agora faz parte de <strong>{joined.coletivo?.nome}</strong>.
            Ele já aparece na página pública do coletivo.
          </p>
          <Link
            to={`/coletivos/${joined.coletivo?.slug}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700"
            data-testid="convite-ver-coletivo"
          >
            Ver página do coletivo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="convite-coletivo-page">
      <div className="bg-gradient-to-r from-purple-700 to-purple-500 text-white">
        <div className="max-w-xl mx-auto px-4 py-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 mb-4 text-xs uppercase font-semibold tracking-wider">
            <Handshake className="w-4 h-4" /> Convite
          </div>
          <h1 className="text-2xl font-bold">Você foi convidado a participar!</h1>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 -mt-4 space-y-4">
        {/* Card do coletivo */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {coletivo.foto_capa && (
            <div className="h-28 bg-cover bg-center" style={{ backgroundImage: `url(${resolveBackendImage(coletivo.foto_capa)})` }} />
          )}
          <div className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-purple-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                {coletivo.foto_perfil ? (
                  <img src={resolveBackendImage(coletivo.foto_perfil)} alt={coletivo.nome} className="w-full h-full object-cover" />
                ) : (
                  <Handshake className="w-7 h-7 text-purple-500" />
                )}
              </div>
              <div>
                <h2 className="font-bold text-lg text-gray-900" data-testid="convite-coletivo-nome">{coletivo.nome}</h2>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                    {TIPO_LABEL[coletivo.tipo] || coletivo.tipo}
                  </span>
                  {coletivo.municipio_nome && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{coletivo.municipio_nome}</span>
                  )}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{coletivo.total_membros_aprovados || 0} membro(s)</span>
                </div>
              </div>
            </div>
            {coletivo.descricao && <p className="text-sm text-gray-600 mt-3">{coletivo.descricao}</p>}
          </div>
        </div>

        {/* Ação */}
        {!user ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center" data-testid="convite-login-required">
            <p className="text-gray-700 font-medium mb-1">Entre para aceitar o convite</p>
            <p className="text-gray-500 text-sm mb-4">
              Faça login (ou crie sua conta) para adicionar seu negócio ao coletivo com 1 clique.
            </p>
            <button
              onClick={() => navigate('/perfil')}
              className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 flex items-center justify-center gap-2"
              data-testid="convite-login-button"
            >
              <LogIn className="w-4 h-4" /> Fazer Login
            </button>
          </div>
        ) : bizLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : myBiz.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center" data-testid="convite-sem-negocio">
            <Store className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-700 font-medium mb-1">Você ainda não tem um negócio cadastrado</p>
            <p className="text-gray-500 text-sm mb-4">
              Cadastre seu negócio primeiro — depois é só voltar neste link para entrar no coletivo.
            </p>
            <button
              onClick={() => navigate('/cadastrar')}
              className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700"
              data-testid="convite-cadastrar-negocio"
            >
              Cadastrar meu negócio
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="convite-escolher-negocio">
            <p className="text-sm font-semibold text-gray-800 mb-3">
              {myBiz.length === 1 ? 'Seu negócio:' : 'Escolha o negócio que vai entrar no coletivo:'}
            </p>
            <div className="space-y-2 mb-4 max-h-72 overflow-y-auto pr-1">
              {[...myBiz]
                .sort((a, b) => {
                  const ea = a.municipio_id === coletivo.municipio_id ? 0 : 1;
                  const eb = b.municipio_id === coletivo.municipio_id ? 0 : 1;
                  return ea - eb;
                })
                .map((b) => {
                const elegivel = b.municipio_id === coletivo.municipio_id;
                return (
                <label
                  key={b.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    !elegivel
                      ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                      : selectedBiz === b.id
                        ? 'border-purple-500 bg-purple-50 cursor-pointer'
                        : 'border-gray-200 hover:border-purple-200 cursor-pointer'
                  }`}
                  data-testid={`convite-negocio-${b.id}`}
                >
                  <input
                    type="radio"
                    name="negocio"
                    checked={selectedBiz === b.id}
                    onChange={() => setSelectedBiz(b.id)}
                    disabled={!elegivel}
                    className="accent-purple-600"
                  />
                  <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center text-purple-700 font-bold flex-shrink-0">
                    {(b.nome || '?').charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-gray-900 truncate">{b.nome}</p>
                    {(b.categoria_nome || b.categoria) && (
                      <p className="text-xs text-gray-500">{b.categoria_nome || b.categoria}</p>
                    )}
                  </div>
                  {!elegivel && (
                    <span className="text-[10px] uppercase font-semibold text-gray-400 flex-shrink-0">
                      outro município
                    </span>
                  )}
                </label>
                );
              })}
            </div>
            <button
              onClick={handleAceitar}
              disabled={submitting || !selectedBiz}
              className="w-full py-3.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="convite-aceitar-button"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Entrar no coletivo
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              Entrada imediata — o convite já vale como aprovação do responsável.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
